import React, { useState } from 'react';
import { printMedicalCertificate } from '../../utils/hospitalPdf';
import { ipApi } from '../../services/api';

const CERT_TYPES = [
  'Fitness certificate',
  'Sick leave certificate',
  'Medical leave certificate',
  'Recovery certificate',
  'Custom certificate',
];

export default function MedicalCertificateModal({ form, doctor, hospital, onClose, persist = false, docTemplates = [] }) {
  const [certType, setCertType] = useState(CERT_TYPES[0]);
  const [templateId, setTemplateId] = useState('');
  const [diagnosis, setDiagnosis] = useState(form.assessment?.primary || form.diagnosis || '');
  const [fromDate, setFromDate] = useState(new Date().toISOString().slice(0, 10));
  const [toDate, setToDate] = useState('');
  const [days, setDays] = useState('3');
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const certPayload = () => ({
    patientName: form.patientName,
    patientId: form.patientId,
    age: form.patientAge || form.age,
    gender: form.patientGender || form.gender,
    certType,
    diagnosis,
    fromDate,
    toDate: toDate || undefined,
    days: Number(days) || undefined,
    remarks,
  });

  const print = async () => {
    if (!diagnosis.trim()) {
      setErr('Diagnosis / condition is required.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const cert = certPayload();
      printMedicalCertificate(cert, hospital, doctor);
      if (persist && (form.patientName || form.patientId)) {
        await ipApi.saveEmr({
          recordId: `CERT-${Date.now()}`,
          patientName: form.patientName,
          patientId: form.patientId || '',
          patientAge: form.patientAge || form.age,
          patientGender: form.patientGender || form.gender,
          visitType: 'OPD',
          status: 'saved',
          assessment: { primary: diagnosis },
          notes: [
            `Medical certificate issued: ${certType}`,
            diagnosis && `Diagnosis: ${diagnosis}`,
            fromDate && `From: ${fromDate}`,
            toDate && `To: ${toDate}`,
            days && `Rest: ${days} day(s)`,
            remarks,
          ].filter(Boolean).join(' · '),
          doctorName: doctor?.name,
          hospitalId: doctor?.hospitalId,
        });
      }
      onClose?.();
    } catch (e) {
      setErr(e.message || 'Failed to save certificate');
    } finally {
      setBusy(false);
    }
  };

  const applyTemplate = (id) => {
    setTemplateId(id);
    const tpl = docTemplates.find((t) => t.id === id);
    if (!tpl?.body) return;
    const body = tpl.body
      .replace(/\{\{patient\}\}/g, form.patientName || '')
      .replace(/\{\{uhid\}\}/g, form.patientId || '')
      .replace(/\{\{diagnosis\}\}/g, diagnosis || form.assessment?.primary || '');
    setRemarks(body);
    if (tpl.name) setCertType(tpl.name);
  };

  return (
    <div className="q-modal-backdrop" onClick={onClose}>
      <div className="q-modal card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <h3 style={{ marginTop: 0, color: '#0d6e6a' }}>Medical certificate</h3>
        <p className="hint">
          {persist ? 'Prints on hospital letterhead and saves a copy to the patient EMR.' : 'Generate fitness / sick leave certificate on letterhead.'}
        </p>
        {err && <div className="error mb">{err}</div>}
        <div className="form-grid">
          {docTemplates.length > 0 && (
            <div className="field full">
              <label>Hospital template</label>
              <select className="input" value={templateId} onChange={(e) => applyTemplate(e.target.value)}>
                <option value="">— Optional template —</option>
                {docTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
          <div className="field full">
            <label>Type</label>
            <select className="input" value={certType} onChange={(e) => setCertType(e.target.value)}>
              {CERT_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="field full">
            <label>Diagnosis / condition *</label>
            <input className="input" required value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} />
          </div>
          <div className="field"><label>From date</label><input className="input" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} /></div>
          <div className="field"><label>To date (optional)</label><input className="input" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} /></div>
          <div className="field"><label>Rest days</label><input className="input" type="number" value={days} onChange={(e) => setDays(e.target.value)} /></div>
          <div className="field full"><label>Remarks</label><textarea className="input" rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} /></div>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={print}>
            {busy ? 'Saving…' : persist ? 'Print & save to EMR' : 'Print certificate'}
          </button>
        </div>
      </div>
    </div>
  );
}
